import { apiRequest, ApiError, getAccessToken, tryRefresh } from "./client";

export type DocumentStatus =
  | "uploading"
  | "processing"
  | "ready"
  | "conversion_error";
export type DocumentFormat = "pdf" | "pptx";

export interface Document {
  id: string;
  title: string;
  owner_user_id: string;
  owner_email: string | null;
  owner_full_name: string | null;
  original_filename: string;
  original_format: DocumentFormat;
  original_size_bytes: number;
  pdf_size_bytes: number | null;
  pages_count: number | null;
  status: DocumentStatus;
  status_error: string | null;
  needs_manual_review: boolean;
  is_template: boolean;
  allow_download: boolean;
  allow_print: boolean;
  created_at: string;
  updated_at: string;
}

export interface DocumentListResponse {
  items: Document[];
  total_count: number;
}

// uploading — внутренний промежуточный статус схемы; на практике бэкенд
// создаёт документ сразу в processing. В UI показываем оба как
// «Обрабатывается», чтобы не выносить технические детали наружу.
export const DOCUMENT_STATUS_LABELS: Record<DocumentStatus, string> = {
  uploading: "Обрабатывается",
  processing: "Обрабатывается",
  ready: "Готово",
  conversion_error: "Ошибка конвертации",
};

/**
 * Превращает status_error из conversion-worker в человеческий текст с
 * рекомендацией. Источники сообщений — backend/workers/conversion.py
 * (soffice, «не содержит страниц», «оригинал не найден», PdfReadError).
 * Сырое сообщение оставьте в title для отладки.
 */
export function humanizeStatusError(raw: string): string {
  const s = raw.toLowerCase();
  if (s.includes("soffice")) {
    return "Не удалось сконвертировать PPTX. Сохраните файл в PDF и загрузите снова.";
  }
  if (s.includes("не содержит страниц")) {
    return "Файл пустой — в нём нет страниц. Загрузите другой документ.";
  }
  if (s.includes("оригинал не найден")) {
    return "Файл оригинала не найден в хранилище. Перезалейте документ.";
  }
  if (s.includes("воркер погибал")) {
    return "Обработка файла несколько раз обрывалась — вероятно, он слишком тяжёлый. Сохраните его в PDF или уменьшите и загрузите снова.";
  }
  return "Не удалось обработать файл — возможно, он повреждён. Попробуйте перезалить.";
}

export interface DocumentListQuery {
  page?: number;
  size?: number;
  owner_user_id?: string;
  is_template?: boolean;
  status?: DocumentStatus;
  needs_review?: boolean;
  search?: string;
}

function buildQuery(query: DocumentListQuery): string {
  const params = new URLSearchParams();
  if (query.page !== undefined) params.set("page", String(query.page));
  if (query.size !== undefined) params.set("size", String(query.size));
  if (query.owner_user_id) params.set("owner_user_id", query.owner_user_id);
  if (query.is_template !== undefined)
    params.set("is_template", String(query.is_template));
  if (query.status) params.set("status", query.status);
  if (query.needs_review !== undefined)
    params.set("needs_review", String(query.needs_review));
  if (query.search) params.set("search", query.search);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export interface UploadPayload {
  file: File;
  title?: string;
  is_template?: boolean;
  allow_download?: boolean;
  allow_print?: boolean;
}

const API_BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "/api/v1";

/**
 * Multipart upload — apiRequest шлёт JSON, поэтому идём напрямую через fetch
 * с теми же conventions (Bearer + silent refresh при 401).
 */
async function uploadOnce(payload: UploadPayload): Promise<Response> {
  const form = new FormData();
  form.append("file", payload.file);
  if (payload.title !== undefined) form.append("title", payload.title);
  if (payload.is_template !== undefined)
    form.append("is_template", String(payload.is_template));
  if (payload.allow_download !== undefined)
    form.append("allow_download", String(payload.allow_download));
  if (payload.allow_print !== undefined)
    form.append("allow_print", String(payload.allow_print));

  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(`${API_BASE}/documents`, {
    method: "POST",
    headers,
    body: form,
    credentials: "include",
  });
}

async function replacePdfOnce(id: string, file: File): Promise<Response> {
  const form = new FormData();
  form.append("file", file);

  const headers: Record<string, string> = {};
  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  return fetch(`${API_BASE}/documents/${id}/replace-pdf`, {
    method: "POST",
    headers,
    body: form,
    credentials: "include",
  });
}

async function parseError(res: Response): Promise<ApiError> {
  let body: { error?: { code: string; message: string; details?: unknown } } = {};
  try {
    body = await res.json();
  } catch {
    /* ignore */
  }
  return new ApiError(
    res.status,
    body.error?.code ?? `http.${res.status}`,
    body.error?.message ?? res.statusText,
    body.error?.details,
  );
}

export const documentsApi = {
  list: (query: DocumentListQuery = {}) =>
    apiRequest<DocumentListResponse>(`/documents${buildQuery(query)}`),
  get: (id: string) => apiRequest<Document>(`/documents/${id}`),
  patch: (
    id: string,
    payload: {
      title?: string;
      is_template?: boolean;
      allow_download?: boolean;
      allow_print?: boolean;
    },
  ) => apiRequest<Document>(`/documents/${id}`, { method: "PATCH", body: payload }),
  delete: (id: string) =>
    apiRequest<void>(`/documents/${id}`, { method: "DELETE" }),
  async upload(payload: UploadPayload): Promise<Document> {
    let res = await uploadOnce(payload);
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        res = await uploadOnce(payload);
      }
    }
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as Document;
  },
  async replacePdf(id: string, file: File): Promise<Document> {
    let res = await replacePdfOnce(id, file);
    if (res.status === 401) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        res = await replacePdfOnce(id, file);
      }
    }
    if (!res.ok) throw await parseError(res);
    return (await res.json()) as Document;
  },
};
